import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  CSSProperties
} from 'react';
import { getDownloadFileUrl } from '@api/file-upload-api.ts';
import Skeleton from '@mui/material/Skeleton';

type MediaType = 'video' | 'audio' | 'iframe';

interface AppMediaProps {
  type: MediaType;
  filePath?: string;
  previewSrc?: string;
  fallback?: string;
  width?: number | string;
  height?: number | string;
  rounded?: string;
  className?: string;
  style?: CSSProperties;
  controls?: boolean;
}

const AppMedia: React.FC<AppMediaProps> = ({
  type,
  filePath,
  previewSrc,
  fallback = 'N/A',
  width = '100%',
  height = 'auto',
  rounded,
  className = '',
  style,
  controls = true
}) => {
  const [mediaSrc, setMediaSrc] = useState<string | null>(previewSrc || null);
  const [loading, setLoading] = useState<boolean>(!!filePath && !previewSrc);
  const [error, setError] = useState<boolean>(false);
  const mediaRef = useRef<HTMLMediaElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useLayoutEffect(() => {
    const media = mediaRef.current;
    const iframe = iframeRef.current;

    return () => {
      if (media) {
        media.pause();
        media.removeAttribute('src');
        media.load();
      }
      if (iframe) iframe.src = 'about:blank';
    };
  }, [mediaSrc, type]);

  useEffect(() => {
    const loadMedia = async () => {
      if (!filePath || previewSrc) return;
      setLoading(true);
      try {
        const { url } = await getDownloadFileUrl(filePath);
        setMediaSrc(url);
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    loadMedia();
  }, [filePath, previewSrc]);

  const roundedClass = rounded ? `rounded-[${rounded}]` : '';
  const sizeStyle: CSSProperties = { width, height, ...style };

  if (loading) {
    return (
      <Skeleton
        variant="rectangular"
        width={width || '100%'}
        height={height || 200}
        animation="wave"
        className={`${roundedClass}`}
      />
    );
  }

  if (error || !mediaSrc) {
    return (
      <div
        className={`flex items-center justify-center bg-gray-100 text-gray-400 text-sm ${roundedClass}`}
        style={sizeStyle}
      >
        {fallback}
      </div>
    );
  }

  switch (type) {
    case 'video':
      return (
        <video
          ref={(el) => {
            mediaRef.current = el;
          }}
          src={mediaSrc}
          autoPlay
          controls={controls}
          className={`w-full h-full bg-black ${roundedClass} ${className}`}
          style={sizeStyle}
        />
      );

    case 'audio':
      return (
        <audio
          ref={(el) => {
            mediaRef.current = el;
          }}
          src={mediaSrc}
          autoPlay
          controls={controls}
          className={`w-full ${className}`}
          style={sizeStyle}
        />
      );

    case 'iframe':
      return (
        <iframe
          ref={iframeRef}
          src={mediaSrc}
          title="Embedded content"
          className={`w-full h-full border-none ${roundedClass} ${className}`}
          style={sizeStyle}
        />
      );

    default:
      return null;
  }
};

export default AppMedia;
